import "@/index.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SiteLayout } from "@/components/site/Layout";
import { AnalyticsTracker } from "@/components/site/AnalyticsTracker";
import { CookieConsent } from "@/components/site/CookieConsent";
import { BrandProvider } from "@/components/site/BrandProvider";
import ProtectedRoute from "@/admin/ProtectedRoute";
import AdminLayout from "@/admin/AdminLayout";

const Home = lazy(() => import("@/pages/Home"));
const Organizza = lazy(() => import("@/pages/Organizza"));
const ItinerariList = lazy(() => import("@/pages/ItinerariList"));
const ItinerarioDetail = lazy(() => import("@/pages/ItinerarioDetail"));
const BudgetList = lazy(() => import("@/pages/BudgetList"));
const BudgetDetail = lazy(() => import("@/pages/BudgetDetail"));
const RisorseList = lazy(() => import("@/pages/RisorseList"));
const RisorsaDetail = lazy(() => import("@/pages/RisorsaDetail"));
const BlogList = lazy(() => import("@/pages/BlogList"));
const ArticoloDetail = lazy(() => import("@/pages/ArticoloDetail"));
const GuideList = lazy(() => import("@/pages/GuideList"));
const GuideDetail = lazy(() => import("@/pages/GuideDetail"));
const BioPage = lazy(() => import("@/pages/BioPage"));
const ChiSono = lazy(() => import("@/pages/ChiSono"));
const Contatti = lazy(() => import("@/pages/Contatti"));
const GenericPage = lazy(() => import("@/pages/GenericPage"));
const NewsletterConfirm = lazy(() => import("@/pages/NewsletterConfirm"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const AuthCallback = lazy(() => import("@/admin/AuthCallback"));
const AdminLogin = lazy(() => import("@/admin/AdminLogin"));
const Dashboard = lazy(() => import("@/admin/Dashboard"));
const ContentList = lazy(() => import("@/admin/ContentList"));
const ContentEditor = lazy(() => import("@/admin/ContentEditor"));
const MediaLibrary = lazy(() => import("@/admin/MediaLibrary"));
const NewsletterList = lazy(() => import("@/admin/NewsletterList"));
const ContactsList = lazy(() => import("@/admin/ContactsList"));
const SettingsEditor = lazy(() => import("@/admin/SettingsEditor"));
const BioEditor = lazy(() => import("@/admin/BioEditor"));
const AdminAnalytics = lazy(() => import("@/admin/AdminAnalytics"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-vs-bg" role="status" aria-live="polite" aria-label="Caricamento in corso" data-testid="page-loader">
      <div className="w-12 h-12 rounded-full border-4 border-vs-green/25 border-t-vs-green animate-spin" />
      <span className="sr-only">Caricamento…</span>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback: session_id arrives in the URL fragment.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<SiteLayout><Home /></SiteLayout>} />
      <Route path="/organizza-il-tuo-viaggio" element={<SiteLayout><Organizza /></SiteLayout>} />
      <Route path="/organizza" element={<Navigate to="/organizza-il-tuo-viaggio" replace />} />
      <Route path="/itinerari" element={<Navigate to="/itinerari-testati" replace />} />
      <Route path="/itinerari-testati" element={<SiteLayout><ItinerariList /></SiteLayout>} />
      <Route path="/itinerari-testati/:slug" element={<SiteLayout><ItinerarioDetail /></SiteLayout>} />
      <Route path="/budget" element={<SiteLayout><BudgetList /></SiteLayout>} />
      <Route path="/budget/:slug" element={<SiteLayout><BudgetDetail /></SiteLayout>} />
      <Route path="/risorse" element={<SiteLayout><RisorseList /></SiteLayout>} />
      <Route path="/risorse/:slug" element={<SiteLayout><RisorsaDetail /></SiteLayout>} />
      <Route path="/blog" element={<SiteLayout><BlogList /></SiteLayout>} />
      <Route path="/blog/:slug" element={<SiteLayout><ArticoloDetail /></SiteLayout>} />
      <Route path="/guide" element={<SiteLayout><GuideList /></SiteLayout>} />
      <Route path="/guide/:slug" element={<SiteLayout><GuideDetail /></SiteLayout>} />
      <Route path="/chi-sono" element={<SiteLayout><ChiSono /></SiteLayout>} />
      <Route path="/contatti" element={<SiteLayout><Contatti /></SiteLayout>} />
      <Route path="/newsletter/conferma" element={<SiteLayout><NewsletterConfirm /></SiteLayout>} />
      <Route path="/pagina/:slug" element={<SiteLayout><GenericPage /></SiteLayout>} />

      <Route path="/bio" element={<BioPage />} />

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute><AdminLayout><Dashboard /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute><AdminLayout><AdminAnalytics /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/content/:ctype" element={<ProtectedRoute><AdminLayout><ContentList /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/content/:ctype/:id" element={<ProtectedRoute><AdminLayout><ContentEditor /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/media" element={<ProtectedRoute><AdminLayout><MediaLibrary /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/newsletter" element={<ProtectedRoute><AdminLayout><NewsletterList /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/contacts" element={<ProtectedRoute><AdminLayout><ContactsList /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute><AdminLayout><SettingsEditor /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/bio" element={<ProtectedRoute><AdminLayout><BioEditor /></AdminLayout></ProtectedRoute>} />

      <Route path="*" element={<SiteLayout><NotFound /></SiteLayout>} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <BrandProvider />
        <AnalyticsTracker />
        <Suspense fallback={<PageLoader />}>
          <AppRouter />
        </Suspense>
        <Toaster position="top-center" richColors />
        <CookieConsent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
